import * as React from 'react'

import { Box, SearchField, type SortDirection, Table } from '@factorialco/deprecated-design-system'
import groupBy from 'lodash/groupBy'

import { observer } from 'mobx-react'

import createMemo from 'modules/core/hooks/createMemo'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useQuestionGroups } from 'modules/reviews/hooks/useQuestionGroups'
import type QuestionGroup from 'modules/reviews/store/models/QuestionGroup'
import type Review from 'modules/reviews/store/models/v2/Review'

import Body from './Body'
import Header from './Header'

function notEmpty<T>(element: T | null | undefined): element is T {
  return element !== null && element !== undefined
}

export function groupQuestionGroups(questionGroups: QuestionGroup[]) {
  return Object.entries(
    groupBy(questionGroups, (questionGroup) => questionGroup.attributes.target_id)
  )
    .flatMap(([_targetId, questionGroups]) => {
      const target = questionGroups[0]!.target

      const groupedByDate = groupBy<QuestionGroup>(
        questionGroups,
        (questionGroup) => questionGroup.attributes.editable_until
      )

      const data = Object.entries(groupedByDate).map(([date, questionGroups]) => {
        const endDate = date === 'null' ? null : stringToDate(date, 'backend_datetime')

        const total = questionGroups.length

        const completion = questionGroups.filter((qg) => qg.isAnswered).length

        const reviewers = questionGroups.map((qg) => qg.respondent).filter(notEmpty)

        return {
          endDate,
          total,
          completion,
          reviewers,
        }
      })

      return data.map((d) => ({
        target,
        ...d,
      }))
    })
    .filter(notEmpty)
}

const useFilter = createMemo(groupQuestionGroups)
interface Props {
  form: Review
}

function ReviewsTable({ form }: Props) {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const { loading, questionGroups } = useQuestionGroups(form)
  const groupedQuestionGroups = useFilter(questionGroups)

  const [direction, setDirection] = React.useState<SortDirection>('asc')
  const [attribute, setAttribute] = React.useState<string>('reviewee')

  const onSort = (attribute: string, direction: SortDirection) => {
    setDirection(direction)
    setAttribute(attribute)
  }

  const TableOptions = () => (
    <Box flexGrow>
      <SearchField
        variant="big"
        search={search}
        onSearch={onSearch}
        placeholder={i18n.t('common.search')}
      />
    </Box>
  )

  return (
    <Table options={TableOptions}>
      <Header form={form} onSort={onSort} direction={direction} attribute={attribute} />
      <Body
        form={form}
        search={debouncedSearch}
        reviews={groupedQuestionGroups}
        loading={loading}
        direction={direction}
      />
    </Table>
  )
}

export default observer(ReviewsTable)
